'use client';
import { type Result } from '@shared/all/domains/request';
import { type SpaceCard as SpaceCardType } from '@shared/all/domains/spaces';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Loader2, Search } from 'lucide-react';
import { useEffect, useState } from 'react';

import useOnModal from '../../hooks/useOnModal';
import Error from '../common/Error';
import Modal, { ModalContent, ModalHeader } from '../HOC/Modal';
import SpaceCard from '../spaces/SpaceCard';
import Input from '../UI/Input';

type BaseSpaceCard = Omit<SpaceCardType, 'pinned' | 'membershipId'> & {
  pinned?: boolean;
  membershipId?: number;
};

type Props = {
  title?: string;
  closeHandler: () => void;
  fetchFunction: (
    cursor: number | undefined,
    search?: string
  ) => Promise<Result<{ spaces: BaseSpaceCard[] }>>;
  onTogglePin?: (space: BaseSpaceCard) => void;
};

const SpacesList = (props: Props) => {
  const [query, setQuery] = useState('');
  const {
    setElement,
    setParentElement,
    isVisible: loadingSectionVisible
  } = useOnModal();

  const parentSectionRef = (node: HTMLElement | null) => {
    setParentElement(node);
  };

  const loadingSectionRef = (node: HTMLElement | null) => {
    setElement(node);
  };

  const { data, fetchNextPage, hasNextPage, isLoading, error, isFetching } =
    useInfiniteQuery({
      queryKey: ['spacesList', props.title ?? 'spaces', query.trim()],
      queryFn: ({ pageParam }) => {
        return unwrapFromResult(
          props.fetchFunction(pageParam, query.trim() || undefined)
        );
      },
      initialPageParam: undefined as number | undefined,
      getNextPageParam: (lastPage) => {
        const spaces = lastPage.spaces ?? [];
        if (spaces.length < 10) return undefined;
        const last = spaces[spaces.length - 1];
        return last?.membershipId ?? last?.id;
      }
    });

  useEffect(() => {
    if (loadingSectionVisible && hasNextPage && !isFetching) fetchNextPage();
  }, [loadingSectionVisible, hasNextPage, isFetching]);

  return (
    <Modal closeHandler={props.closeHandler}>
      <ModalHeader>
        <h2>{props.title ?? 'Spaces'}</h2>
      </ModalHeader>
      <ModalContent>
        <div className="mb-3">
          <Input
            value={query}
            onChange={setQuery}
            placeholder="Search spaces…"
            icons={[
              <Search key="search" size={16} className="text-textSecondary" />
            ]}
          />
        </div>
        <div ref={parentSectionRef} className="h-80 overflow-y-auto">
          {(() => {
            const allSpaces =
              data?.pages.flatMap((page) => page.spaces ?? []) ?? [];
            const lowerQuery = query.toLowerCase().trim();
            const filtered = lowerQuery
              ? allSpaces.filter(
                  (s) =>
                    s.name.toLowerCase().includes(lowerQuery) ||
                    s.spaceSlug.toLowerCase().includes(lowerQuery)
                )
              : allSpaces;
            return (
              <>
                {!isLoading &&
                  !error &&
                  filtered.map((space) => (
                    <SpaceCard
                      key={space.id}
                      avatar={{
                        spaceAvatarKey: space.avatarKey
                      }}
                      href={`/s/${space.spaceSlug}`}
                      name={space.name}
                      description={space.description}
                      pinned={space.pinned ?? false}
                      onTogglePin={
                        props.onTogglePin
                          ? () => props.onTogglePin?.(space)
                          : undefined
                      }
                    />
                  ))}
                {!isLoading && !error && filtered.length === 0 && (
                  <p className="text-center text-textSecondary/70">
                    {lowerQuery
                      ? 'No spaces match your search.'
                      : 'No Spaces found.'}
                  </p>
                )}
              </>
            );
          })()}
          <Error error={error} loading={isLoading} />
          <div
            className={`flex w-full justify-center ${hasNextPage ? 'block' : 'hidden'}`}
            ref={loadingSectionRef}
          >
            <Loader2 className="animate-spin" height={50} width={50} />
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default SpacesList;
